import type { AppRouter } from '@app/api'
import { type Board, decodeBoard, standingIn } from '@app/shared'
import { appKey } from '@app/shared/brand'
import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { useEffect, useState } from 'react'
import { BUILD_COMMIT } from '../../lib/build'
import type { OrgListItem } from '../../lib/org'
import { useTRPC } from '../../lib/trpc'
import { BoardGrid } from './BoardGrid'
import {
  type Ahead,
  BOARD_REREAD_MS,
  covering,
  markNow,
  momentOf,
  SAMPLE_BOARD,
  shownLeading,
} from './board-moment'
import { FullScreen } from './FullScreen'
import './board.css'

/** How often the board asks again where the shul stands. */
const AHEAD_REREAD_MS = 60 * 60_000
const VERSION_REREAD_MS = 5 * 60_000
const AHEAD_GRAIN_MS = 15 * 60_000

type Outputs = inferRouterOutputs<AppRouter>
type ScreenBoard = Outputs['board']['forScreen']
type CachedScreen = {
  board: ScreenBoard | null
  ahead: Ahead | null
  shul: OrgListItem | null
}

const emptyCache = (): CachedScreen => ({
  board: null,
  ahead: null,
  shul: null,
})
const screenKey = (orgId: string, boardId: string) =>
  appKey('board-screen', orgId, boardId)

function readCache(key: string): CachedScreen {
  try {
    const value = localStorage.getItem(key)
    return value ? (JSON.parse(value) as CachedScreen) : emptyCache()
  } catch {
    return emptyCache()
  }
}

function writeCache(key: string, patch: Partial<CachedScreen>) {
  try {
    localStorage.setItem(key, JSON.stringify({ ...readCache(key), ...patch }))
  } catch {
    // A screen still runs in private or storage-constrained browsers.
  }
}

/**
 * A screen in the shul's building, showing one board.
 *
 * A saved board is read from its permanent address and followed once a minute.
 * Without that id, the board is what the link carries — the whole of it, in the
 * fragment — so prototype and preview links keep working. `?at=` pins the
 * moment the board starts from, and the clock runs on from there.
 *
 * Every time drawn here is the server's, from the one public read the shul's
 * home page leads with; the board computes none.
 */
export function BoardPage({
  orgId,
  boardId,
}: {
  orgId: string
  boardId?: string
}) {
  const trpc = useTRPC()
  const cacheKey = boardId ? screenKey(orgId, boardId) : ''
  const preview = new URLSearchParams(window.location.search).has('preview')
  const [cached, setCached] = useState<CachedScreen>(() =>
    cacheKey ? readCache(cacheKey) : emptyCache(),
  )
  const orgs = useQuery(trpc.org.list.queryOptions())
  const liveShul = orgs.data?.find((s) => s.shortId === orgId)
  const shul = liveShul ?? cached.shul ?? undefined
  const saved = useQuery({
    ...trpc.board.forScreen.queryOptions({
      orgShortId: orgId,
      boardShortId: boardId ?? '',
      preview,
    }),
    enabled: !!boardId,
    refetchInterval: BOARD_REREAD_MS,
    initialData: cached.board ?? undefined,
  })

  useEffect(() => {
    if (!cacheKey || !saved.data) return
    writeCache(cacheKey, { board: saved.data })
    setCached((current) => ({ ...current, board: saved.data }))
  }, [cacheKey, saved.data])

  useEffect(() => {
    if (!cacheKey || !liveShul) return
    writeCache(cacheKey, { shul: liveShul })
    setCached((current) => ({ ...current, shul: liveShul }))
  }, [cacheKey, liveShul])

  const [linkBoard, setLinkBoard] = useState<Board>(() => boardFromHash())
  useEffect(() => {
    if (boardId) return
    const read = () => setLinkBoard(boardFromHash())
    window.addEventListener('hashchange', read)
    return () => window.removeEventListener('hashchange', read)
  }, [boardId])

  const now = useNow()
  // The read moves once a minute, not once a second: its answer turns at a
  // minute's grain, and a query key that changed every tick would refetch
  // every tick.
  const minute = new Date(Math.floor(now / BOARD_REREAD_MS) * BOARD_REREAD_MS)
  const leading = useQuery({
    ...trpc.occasion.leading.queryOptions({
      spaceId: shul?.spaceId ?? '',
      at: minute.toISOString(),
    }),
    enabled: !!shul,
    placeholderData: (previous) => previous,
  })
  const [aheadFrom, setAheadFrom] = useState(() => aheadStart())
  useEffect(() => {
    const id = window.setInterval(
      () => setAheadFrom(aheadStart()),
      AHEAD_REREAD_MS,
    )
    return () => window.clearInterval(id)
  }, [])
  const ahead = useQuery({
    ...trpc.board.ahead.queryOptions({
      orgShortId: orgId,
      boardShortId: boardId ?? '',
      from: aheadFrom,
    }),
    enabled: !!boardId && !!shul,
    refetchInterval: AHEAD_REREAD_MS,
  })
  useEffect(() => {
    if (!cacheKey || !ahead.data) return
    writeCache(cacheKey, { ahead: ahead.data })
    setCached((current) => ({ ...current, ahead: ahead.data }))
  }, [ahead.data, cacheKey])

  const online = useOnline()
  useBoardWorker(!!boardId)
  useBoardVersion(online && !!boardId)

  if (boardId && (!saved.data || !shul) && (saved.isLoading || orgs.isLoading))
    return null

  // A failed re-read keeps the board it already has: a network blip must not
  // blank a screen in the lobby. Only a board never read says it is missing.
  if (boardId && saved.isError && !saved.data) {
    return (
      <div className="display-board display-board--empty">
        No board at this address
      </div>
    )
  }

  if (!shul) {
    return (
      <div className="display-board display-board--empty">
        {orgs.isLoading ? null : 'No congregation at this address'}
      </div>
    )
  }

  const held = covering(ahead.data ?? cached.ahead, now)
  const displayedLeading = shownLeading(
    leading.data ?? null,
    held && shul ? markNow(held, now, shul.timeZoneId) : null,
    { error: leading.isError, placeholder: leading.isPlaceholderData },
  )
  const moment = displayedLeading ? momentOf(displayedLeading) : null
  const standInId =
    saved.data && moment ? standingIn(saved.data.standIns, moment) : null
  const shownBoard =
    saved.data?.standIns.find((standIn) => standIn.boardId === standInId)
      ?.definition ??
    saved.data?.definition ??
    linkBoard
  const boardOnline =
    online && !saved.isError && !leading.isError && !ahead.isError

  return (
    <BoardGrid
      board={shownBoard}
      shul={shul}
      now={now}
      leading={displayedLeading}
      moment={moment}
      orgShortId={orgId}
      boardShortId={boardId ?? null}
      codes={new URLSearchParams(window.location.search).has('codes')}
      online={boardOnline}
    >
      <FullScreen />
      {!boardOnline ? (
        <span
          className="display-board__offline"
          role="img"
          aria-label="Using held data"
        />
      ) : null}
    </BoardGrid>
  )
}

const aheadStart = () =>
  new Date(
    Math.floor(Date.now() / AHEAD_GRAIN_MS) * AHEAD_GRAIN_MS,
  ).toISOString()

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const read = () => setOnline(navigator.onLine)
    window.addEventListener('online', read)
    window.addEventListener('offline', read)
    return () => {
      window.removeEventListener('online', read)
      window.removeEventListener('offline', read)
    }
  }, [])
  return online
}

function useBoardWorker(enabled: boolean) {
  useEffect(() => {
    if (!enabled || !('serviceWorker' in navigator)) return
    let dropped = false
    void navigator.serviceWorker
      .register('/board-sw.js', { scope: '/' })
      .then(() => navigator.serviceWorker.ready)
      .then((registration) => {
        if (!dropped)
          registration.active?.postMessage({
            kind: 'hold-board-shell',
            path: window.location.pathname,
            assets: performance
              .getEntriesByType('resource')
              .map((entry) => entry.name),
          })
      })
      .catch(() => {})
    return () => {
      dropped = true
    }
  }, [enabled])
}

function useBoardVersion(enabled: boolean) {
  useEffect(() => {
    if (!enabled || BUILD_COMMIT === 'unknown') return
    let dropped = false
    const check = async () => {
      if (!navigator.onLine) return
      try {
        // The bundle's own commit: the API deploys on its own and may differ.
        const response = await fetch('/version.json', { cache: 'no-store' })
        if (!response.ok) return
        const { commit } = (await response.json()) as { commit?: unknown }
        if (
          !dropped &&
          navigator.onLine &&
          typeof commit === 'string' &&
          commit !== 'unknown' &&
          commit !== BUILD_COMMIT
        )
          window.location.reload()
      } catch {
        // An outage never triggers the reload that could strand the screen.
      }
    }
    void check()
    const id = window.setInterval(check, VERSION_REREAD_MS)
    return () => {
      dropped = true
      window.clearInterval(id)
    }
  }, [enabled])
}

function boardFromHash(): Board {
  const fragment = window.location.hash.replace(/^#/, '')
  return (fragment && decodeBoard(fragment)) || SAMPLE_BOARD
}

/**
 * The board's clock, in milliseconds: the real one, or one started from the
 * moment `?at=` pins and running on at the real rate.
 */
function useNow(): number {
  const [offset] = useState(() => {
    const at = new URLSearchParams(window.location.search).get('at')
    const pinned = at ? Date.parse(at) : Number.NaN
    return Number.isNaN(pinned) ? 0 : pinned - Date.now()
  })
  const [now, setNow] = useState(() => Date.now() + offset)
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now() + offset), 1000)
    return () => window.clearInterval(id)
  }, [offset])
  return now
}
